import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import EmployeeForm, { DepartmentManagerEmployeeFields } from '../components/EmployeeForm';
import Dropdown from '../components/Dropdown';
import { CONTROL_CLASSES } from '../components/FormField';
import {
  DEPARTMENT_MANAGER_EDITABLE_FIELDS,
  canEditAnyEmployeeField,
  canEditEmployee,
  canEditEmployees,
  employeeUpdateErrorMessage,
} from '../utils/employeePermissions';

const needsAccountDetails = (pm) =>
  pm.requires_account_number || pm.requires_account_title || pm.requires_bank_name;

// Summary-table cell: '-' for a field this method does not use, 'Not set' only for
// a field it requires that is still empty.
const detailCell = (required, value) => {
  if (!required) return <span className="text-gray-400">-</span>;
  return value || <span className="text-gray-400">Not set</span>;
};

// HR/admin (employees:update_all): every field and payment methods. Documents are managed
// in the Documents section of the employee's profile, not here.
const FullEditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const methods = useForm();
  const { handleSubmit, reset, formState: { isSubmitting } } = methods;
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [employeePaymentMethods, setEmployeePaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState(null);
  
  useEffect(() => {
    if (!id || isNaN(Number(id))) return;
    // Only the latest run may populate the form. StrictMode runs this effect twice
    // in dev and an id change re-runs it, so a stale response could land after the
    // user has started typing. Every state the reset effect below depends on is
    // also set in ONE batch after ONE await: the employee and its payment methods
    // used to arrive separately, re-running reset() (and discarding edits) in
    // between.
    let cancelled = false;

    const fetchEmployee = async () => {
      const response = await api.get(`/api/employees/${id}`);
      const emp = response.data.data;
      Object.keys(emp).forEach(key => {
        if (typeof emp[key] === 'string') emp[key] = emp[key].trim();
      });
      return emp;
    };

    const fetchEmployeePaymentMethods = async () => {
      try {
        const response = await api.get(`/api/employees/${id}/payment-methods`);
        return response.data.data;
      } catch (error) {
        console.error('Failed to fetch employee payment methods:', error);
        return [];
      }
    };

    const fetchData = async () => {
      let results;
      try {
        results = await Promise.all([
          api.get('/api/departments'),
          api.get('/api/employees', { params: { status: 'active', limit: 2000 } }),
          api.get('/api/payment-methods'),
          api.get('/api/banks'),
          // Settled rather than thrown, so a missing employee keeps its own message
          // and redirect instead of the generic one.
          fetchEmployee().then((emp) => ({ emp }), (error) => ({ error })),
          fetchEmployeePaymentMethods(),
        ]);
      } catch (error) {
        if (!cancelled) toast.error('Failed to fetch data');
        return;
      }
      if (cancelled) return;

      const [deptRes, empRes, pmRes, bankRes, employeeResult, empPaymentMethods] = results;
      if (employeeResult.error) {
        toast.error('Failed to fetch employee');
        navigate('/employees');
        return;
      }
      setDepartments(deptRes.data.data);
      setEmployees(empRes.data.data || []);
      setPaymentMethods(pmRes.data.data);
      setBanks(bankRes.data.data);
      setEmployeePaymentMethods(empPaymentMethods);
      setEmployee(employeeResult.emp);
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (employee && paymentMethods.length > 0 && Array.isArray(employeePaymentMethods) && banks.length > 0 && departments.length > 0) {
      // Build initial form state
      const initialFormState = { ...employee, payment_methods: {} };
      paymentMethods.forEach(pm => {
        const empPm = employeePaymentMethods.find(e => e.payment_method_id === pm.id);
        if (empPm) {
          initialFormState.payment_methods[pm.id] = {
            ...empPm,
            bank_id: empPm.bank_id || ''
          };
        }
      });
      reset(initialFormState);
    }
    // eslint-disable-next-line
  }, [employee, paymentMethods, employeePaymentMethods, banks, departments, reset]);

  const onSubmit = async (data) => {
    try {
      // Transform payment_methods from nested object to array
      let paymentMethodsArray = [];
      if (data.payment_methods && typeof data.payment_methods === 'object') {
        paymentMethodsArray = Object.entries(data.payment_methods).map(([pmId, details]) => ({
          payment_method_id: Number(pmId),
          ...details
        }));
      }
      // A per-person exception, allowed on any employment type. Blank sends null,
      // which means "follow the attendance policy for this employment type".
      const hasTargetHours = data.daily_target_hours !== '' && data.daily_target_hours != null;
      const targetHours = hasTargetHours ? Number(data.daily_target_hours) : null;
      const submitData = {
        ...data,
        payment_methods: paymentMethodsArray,
        daily_target_hours: targetHours,
        attendance_exempt: !!data.attendance_exempt,
      };
      await api.put(`/api/employees/${id}`, submitData);

      toast.success('Employee updated!');

      navigate(`/employees/${id}`);
    } catch (err) {
      toast.error(employeeUpdateErrorMessage(err));
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-6">Edit Employee</h2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <EmployeeForm 
            departments={departments} 
            employees={employees} 
            mode="edit" 
            currentEmployeeId={Number(id)}
          />
          
          {/* Payment Method Details Section */}
          <div className="mt-8 border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Payment Method Details</h3>
            <table className="min-w-full mb-6">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Account Number</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Account Title</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                </tr>
              </thead>
              <tbody>
                {paymentMethods.map(pm => {
                  const empPm = employeePaymentMethods.find(e => e.payment_method_id === pm.id);
                  return (
                    <tr key={pm.id}>
                      <td className="px-2 py-1">{pm.name}</td>
                      <td className="px-2 py-1 text-gray-900">{detailCell(pm.requires_account_number, empPm?.account_number)}</td>
                      <td className="px-2 py-1 text-gray-900">{detailCell(pm.requires_account_title, empPm?.account_title)}</td>
                      <td className="px-2 py-1 text-gray-900">{detailCell(pm.requires_bank_name, banks.find(b => b.id === empPm?.bank_id)?.name)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Payment Method Configuration. A method that needs no account
                details (e.g. Deel) has nothing to fill in, so it gets no card. */}
            {paymentMethods.filter(needsAccountDetails).map(pm => {
              const empPm = employeePaymentMethods.find(e => e.payment_method_id === pm.id);
              return (
                <div key={pm.id} className="border rounded-lg p-4 mb-4">
                  <h4 className="font-medium mb-3">{pm.name}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pm.requires_account_number && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                        <input
                          type="text"
                          {...methods.register(`payment_methods.${pm.id}.account_number`)}
                          defaultValue={empPm?.account_number || ''}
                          className={CONTROL_CLASSES}
                        />
                      </div>
                    )}
                    {pm.requires_account_title && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Account Title</label>
                        <input
                          type="text"
                          {...methods.register(`payment_methods.${pm.id}.account_title`)}
                          defaultValue={empPm?.account_title || ''}
                          className={CONTROL_CLASSES}
                        />
                      </div>
                    )}
                    {pm.requires_bank_name && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bank</label>
                        <Dropdown
                          {...methods.register(`payment_methods.${pm.id}.bank_id`)}
                        >
                          <option value="">Select bank</option>
                          {banks.map(bank => (
                            <option key={bank.id} value={bank.id}>{bank.name}</option>
                          ))}
                        </Dropdown>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          <button type="submit" disabled={isSubmitting} data-testid="employee-form-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? 'Updating...' : 'Update Employee'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

// Everyone below update_all. The one write left is employees:update_department (a
// department manager): someone else in their own department, and only
// DEPARTMENT_MANAGER_EDITABLE_FIELDS. The server answers any other key with 403
// FIELDS_NOT_EDITABLE and writes nothing, so nothing else is shown, fetched or sent.
// update_own (every employee) admits no edit at all and is turned away.
const DepartmentManagerEditEmployee = () => {
  const { hasPermission, user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const methods = useForm();
  const { handleSubmit, reset, formState: { isSubmitting } } = methods;
  const [employee, setEmployee] = useState(null);

  const mayEditSomeone = canEditEmployees(hasPermission);
  const allowed = employee ? canEditEmployee(hasPermission, user, employee) : mayEditSomeone;

  useEffect(() => {
    if (allowed) return;
    toastPermissionDenied(employee
      ? 'You do not have permission to edit this employee'
      : 'You do not have permission to edit employees');
    navigate('/employees');
  }, [allowed, employee, navigate]);

  useEffect(() => {
    if (!mayEditSomeone || !id || Number.isNaN(Number(id))) return undefined;
    let cancelled = false;
    api.get(`/api/employees/${id}`).then(
      (response) => {
        if (cancelled) return;
        const emp = response.data.data;
        const values = {};
        DEPARTMENT_MANAGER_EDITABLE_FIELDS.forEach((field) => {
          values[field] = typeof emp[field] === 'string' ? emp[field].trim() : emp[field];
        });
        reset(values);
        setEmployee(emp);
      },
      () => {
        if (cancelled) return;
        toast.error('Failed to fetch employee');
        navigate('/employees');
      }
    );
    return () => {
      cancelled = true;
    };
  }, [id, mayEditSomeone, navigate, reset]);

  const onSubmit = async (data) => {
    const body = {};
    DEPARTMENT_MANAGER_EDITABLE_FIELDS.forEach((field) => {
      body[field] = data[field];
    });
    try {
      await api.put(`/api/employees/${id}`, body);
      toast.success('Employee updated!');
      navigate(`/employees/${id}`);
    } catch (err) {
      toast.error(employeeUpdateErrorMessage(err));
    }
  };

  if (!employee || !allowed) {
    return (
      <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-soft">
      <h2 className="text-2xl font-bold mb-2">Edit Employee</h2>
      <p className="text-sm text-gray-600 mb-6">
        {employee.first_name} {employee.last_name}. As their department manager you can update
        their position and contact details. Anything else is changed by HR.
      </p>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <DepartmentManagerEmployeeFields />
          <button type="submit" disabled={isSubmitting} data-testid="employee-form-submit" className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors">
            {isSubmitting ? 'Updating...' : 'Update Employee'}
          </button>
        </form>
      </FormProvider>
    </div>
  );
};

const EditEmployee = () => {
  const { hasPermission } = useAuth();
  return canEditAnyEmployeeField(hasPermission) ? <FullEditEmployee /> : <DepartmentManagerEditEmployee />;
};

export default EditEmployee; 